import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CommunityStories = () => {
  const [activeStory, setActiveStory] = useState(0);

  const stories = [
  {
    id: 1,
    title: "From Fisherman to Forest Guardian",
    author: "Ricardo Santos",
    role: "Community Ranger",
    location: "Barangay Cabayugan",
    date: "2025-11-15",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11cc0d1f9-1764781647308.png",
    imageAlt: "Filipino man in his fifties wearing ranger uniform standing proudly in front of dense tropical forest with binoculars around neck and conservation badge on chest",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1da8be68d-1763292945994.png",
    authorImageAlt: "Headshot of middle-aged Filipino man with warm smile wearing olive green ranger uniform and cap",
    excerpt: "After 30 years of fishing, I discovered my true calling protecting the forest that sustains our community.",
    content: `Growing up in Cabayugan, I spent three decades as a fisherman, following in my father's footsteps. But overfishing and changing weather patterns made it harder each year to support my family.\n\nIn 2020, I joined the park's community ranger program. The training opened my eyes to how the forest and river ecosystem directly impacts our fishing grounds. Healthy forests mean healthy rivers, which mean abundant fish.\n\nNow, I patrol the buffer zones, educate fellow fishermen about sustainable practices, and help monitor wildlife. My income is stable, and I'm teaching my children that conservation isn't just about protecting nature—it's about protecting our future.\n\nThe park gave me more than a job; it gave me purpose. Every tree I help protect, every poacher I deter, I'm ensuring my grandchildren will inherit the same natural wealth I enjoyed as a child.`,
    impact: "Trained 45 community members in sustainable fishing practices",
    category: "Livelihood Transformation"
  },
  {
    id: 3,
    title: "Youth Leading Conservation Innovation",
    author: "Jasmine Cruz",
    role: "Youth Conservation Leader",
    location: "Puerto Princesa City",
    date: "2025-11-30",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11cc0d1f9-1764781647308.png",
    imageAlt: "Young Filipino woman in her twenties wearing conservation t-shirt leading group of teenagers in environmental education activity with tablets and field guides in forest clearing",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_192173fcd-1763297914740.png",
    authorImageAlt: "Professional headshot of young Filipino woman with long black hair wearing bright smile and conservation organization polo shirt",
    excerpt: "Technology and traditional knowledge combine as young people create innovative solutions for conservation challenges.",
    content: `At 24, I'm part of a generation that grew up with smartphones and social media, but also witnessed firsthand the impacts of climate change on our community. When I graduated with a degree in Environmental Science, I knew I wanted to work locally.\n\nThe park's Youth Innovation Program gave me the platform to launch 'EcoGuardians'—a mobile app where community members report illegal activities, document wildlife sightings, and share conservation tips. Within six months, we had 2,000 active users.\n\nWe're also running a social media campaign that's changed how young Filipinos view conservation careers. Instead of seeing it as sacrifice, they see it as innovation, adventure, and meaningful work. Our TikTok videos about endemic species have reached over 500,000 views.\n\nThe best part? We're creating jobs. Five of my college friends now work in conservation tech, eco-tourism, and environmental education. We're proving that protecting nature and building careers aren't mutually exclusive—they're interdependent.`,
    impact: "Engaged 2,000+ youth in conservation activities through digital platforms",
    category: "Innovation & Technology"
  },
  {
    id: 4,
    title: "Women Empowerment Through Eco-Enterprise",
    author: "Elena Domingo",
    role: "Cooperative President",
    location: "Barangay Tagabinet",
    date: "2025-11-20",
    image: "https://images.unsplash.com/photo-1677146337058-b24ac3b053ad",
    imageAlt: "Group of Filipino women in colorful traditional clothing working together weaving sustainable handicrafts from natural materials in bright community workshop with finished products displayed",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_16ff45a2b-1763301418862.png",
    authorImageAlt: "Confident middle-aged Filipino woman with short hair wearing traditional woven vest and warm smile showing leadership presence",
    excerpt: "Our women's cooperative transforms sustainable forest products into economic opportunities while preserving traditional crafts.",
    content: `Ten years ago, I was a housewife dependent on my husband's irregular income from logging. When the park implemented stricter protection measures, many families struggled. But the park didn't just enforce rules—they offered alternatives.\n\nThey helped us form a women's cooperative focused on sustainable handicrafts using fallen branches, natural dyes, and non-timber forest products. They provided training in business management, quality control, and marketing.\n\nToday, our cooperative has 45 members generating an average income of ₱15,000 monthly—more than double what our husbands earned from logging. We sell our products at the visitor center, online, and to Manila boutiques.\n\nBut the impact goes beyond money. Women who never left their homes now attend trade fairs in Manila. Daughters who would have dropped out of school are now in college. We've proven that conservation creates opportunities, especially for women.\n\nOur success inspired three other communities to start similar cooperatives. We're not just protecting the forest—we're building a new economy based on its sustainable use.`,
    impact: "Generated ₱6.75M in sustainable income for 45 families", category: "Economic Empowerment"
  }];


  const currentStory = stories?.[activeStory];

  return (
    <section className="py-16 bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Community Stories
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            Real voices from the frontlines of conservation. Discover how local communities are transforming lives while protecting our natural heritage.
          </p>
        </div>

        {/* Story Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {stories?.map((story, index) =>
          <button
            key={story?.id}
            onClick={() => setActiveStory(index)}
            className={`text-left p-4 rounded-lg transition-all ${
            activeStory === index ?
            'bg-primary text-white shadow-conservation' :
            'bg-background text-gray-400 hover:bg-muted'}`
            }>

              <div className="flex items-center gap-3 mb-2">
                <Image
                src={story?.authorImage}
                alt={story?.authorImageAlt}
                className="w-12 h-12 rounded-full object-cover" />

                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{story?.author}</div>
                  <div className="text-xs opacity-80 truncate">{story?.role}</div>
                </div>
              </div>
              <div className="text-sm font-medium line-clamp-2">{story?.title}</div>
            </button>
          )}
        </div>

        {/* Featured Story */}
        <div className="bg-background rounded-lg overflow-hidden shadow-conservation-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Image Section */}
            <div className="relative h-[400px] lg:h-auto overflow-hidden">
              <Image
                src={currentStory?.image}
                alt={currentStory?.imageAlt}
                className="w-full h-full object-cover" />

              <div className="absolute top-4 left-4 bg-accent text-black px-4 py-2 rounded-full text-sm font-medium">
                {currentStory?.category}
              </div>
            </div>

            {/* Content Section */}
            <div className="p-8 lg:p-12 flex flex-col">
              {/* Author Info */}
              <div className="flex items-center gap-4 mb-6">
                <Image
                  src={currentStory?.authorImage}
                  alt={currentStory?.authorImageAlt}
                  className="w-16 h-16 rounded-full object-cover" />

                <div>
                  <h3 className="text-xl font-bold text-white">{currentStory?.author}</h3>
                  <p className="text-gray-400">{currentStory?.role}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                    <span className="flex items-center gap-1">
                      <Icon name="MapPin" size={14} />
                      {currentStory?.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      {new Date(currentStory.date)?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Story Title */}
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                {currentStory?.title}
              </h2>

              {/* Excerpt */}
              <p className="text-lg text-accent mb-6 italic">
                "{currentStory?.excerpt}"
              </p>

              {/* Story Content */}
              <div className="flex-1 overflow-y-auto max-h-[300px] mb-6 pr-2 custom-scrollbar">
                {currentStory?.content?.split('\n\n')?.map((paragraph, index) =>
                <p key={index} className="text-gray-300 mb-4 leading-relaxed">
                    {paragraph}
                  </p>
                )}
              </div>

              {/* Impact Badge */}
              <div className="flex items-start gap-3 p-4 bg-success/10 rounded-lg mb-6">
                <Icon name="TrendingUp" size={20} className="text-success mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-success mb-1">Community Impact</div>
                  <div className="text-sm text-gray-300">{currentStory?.impact}</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button variant="default">
                  Share Your Story
                </Button>
                <Button variant="outline">
                  Read More Stories
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Story Navigation Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {stories?.map((_, index) =>
          <button
            key={index}
            onClick={() => setActiveStory(index)}
            className={`w-3 h-3 rounded-full transition-all ${
            activeStory === index ?
            'bg-accent w-8' : 'bg-gray-600 hover:bg-gray-500'}`
            }
            aria-label={`Go to story ${index + 1}`} />

          )}
        </div>
      </div>
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: var(--color-muted);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: var(--color-primary);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: var(--color-accent);
        }
      `}</style>
    </section>);

};

export default CommunityStories;